import axios, { AxiosError } from 'axios';

export class PrinterError extends Error {
  constructor(code: number | undefined, message: string) {
    super(message);
    this.name = 'PrinterError';
  }
}

export interface PrinterStatus {
  error?: {
    code: number;
    description: string;
  };
  status: string;
  id?: string;
}

export interface PrinterStatusResponse {
  device?: PrinterStatus;
  request?: PrinterStatus;
}

const EXCEPTION_CODE_TOO_MANY_REQUESTS = 2;
const EXCEPTION_CODE_MULTIPLE_ACCESS = 6;
const EXCEPTION_CODE_MESSAGES: Record<number, string> = {
  [EXCEPTION_CODE_MULTIPLE_ACCESS]:
    'Drukarka nie wykonała poprzedniej operacji. Możliwe, że jest już w trakcie drukowania innego dokumentu. Proszę spróbować ponownie za chwilę.',
  [EXCEPTION_CODE_TOO_MANY_REQUESTS]:
    'Osiągnięto limit pobrań tokenu. Zresetuj drukarkę i spróbuj ponownie.',
};

const CODE_NO_PAPER = 1;
const CODE_PRINTER_HEAD_OPEN = 2;
const DEVICE_STATUS_MESSAGES: Record<number, string> = {
  [CODE_NO_PAPER]: 'Brak papieru w drukarce',
  [CODE_PRINTER_HEAD_OPEN]: 'Otwarta głowica drukarki',
};

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      message?: string;
      exception?: { code?: number; description?: string };
    }>;
    const data = axiosError.response?.data;
    if (data?.message) {
      return data.message;
    }
    const exceptionCode = data?.exception?.code;
    if (exceptionCode !== undefined) {
      const mapped = EXCEPTION_CODE_MESSAGES[exceptionCode];
      if (mapped) {
        return mapped;
      }
    }
    if (axiosError.response?.statusText) {
      return `${axiosError.response.status} ${axiosError.response.statusText}`;
    }
    if (axiosError.message) {
      if (axiosError.message.includes('Network Error')) {
        return 'Błąd połączenia z drukarką.';
      }
      return axiosError.message;
    }
    return 'Błąd połączenia z drukarką';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

export function parsePrinterError(error: PrinterStatusResponse): PrinterError {
  let additionalInfo = '';
  if (error.request?.error?.code === 18) {
    additionalInfo =
      '. Usługa była sprzedana z inną stawką VAT niż ta, która jest aktualnie ustawiona.';
  }
  return new PrinterError(
    error.request?.error?.code,
    (error.request?.error?.description || 'Błąd drukarki') + additionalInfo,
  );
}

export function mapDeviceStatusToMessage(status: PrinterStatus): string {
  if (status.error) {
    const mapped = DEVICE_STATUS_MESSAGES[status.error.code];
    if (mapped) {
      return mapped;
    }
    return `Błąd drukarki (kod: ${status.error.code}): ${status.error.description}`;
  }
  return '';
}
